/**
 * The Chart workspace timeline panel (W2-03 note-lane; slot `chart.timeline`; ARCHITECTURE.md §7.7, DESIGN.md §4.5).
 *
 * Mounts one TimelineHost (W1-15) with every module's column factories and decoration providers, hands it to the
 * services (`attachTimeline`) and keeps it in sync, so React never renders per frame:
 *   - view: the editor store's timeline view, snap and the settings' playhead position go to `setView`; the host's
 *     own scroll / zoom / Alt+wheel snap changes come back through `onViewChange`;
 *   - redraws: document ChangeSets (`applyChangeSet`), the engine revision, editor-store changes that alter what a
 *     lane shows (selection, hover, placements, tool, snap, lane X view) and relayouts when the column set changes
 *     (active line, pinned lanes and columns, motion columns, layers);
 *   - playback: playing state and the playhead (a 'transport' frame callback; on demand while paused);
 *   - audio: the waveform from the audio worker's peak pyramid (W2-08's `peaks`; the PCM is installed here when the
 *     worker does not have it yet), the overview strip's note and key density;
 *   - events: gutter / overview seeks to the transport, marquees and time-range selections to the editor selection
 *     (live, relative to the selection at the start), the loop drag to the editor loop, the time gutter's context menu
 *     (insert / remove time, markers), column widths to the user settings;
 *   - focus regions: column instances declare their region (lane / motion / curve; TimelineColumnInstance.region,
 *     put on the column element by the host); the host's own gutter and waveform columns count as the lane. Column
 *     elements are focused on press, so `focus.lane` and `focus.motion` follow clicks (K, G and Ctrl+A act on the
 *     focused region).
 * Column headers are React components portaled into the host's header slots.
 */

import { useEffect, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { Beats } from '../../core/time/beat';
import type { ChangeSet } from '../../core/ops/types';
import type { AppServices, PanelProps } from '../../app/registry/types';
import type { EditorStore, Selection } from '../../state/types';
import { Sel } from '../../state/selection';
import { useT } from '../../i18n/useT';
import { EmptyState } from '../../ui/EmptyState';
import type { TimelineViewConfig } from '../../render/types';
import {
  createTimelineHost,
  type ColumnLayout,
  type TimelineHostController,
  type TimelineHostOptions,
} from '../../render/timeline/TimelineHost';
import { createWaveformSource } from '../../render/timeline/waveformColumn';
import { m } from './noteLane.messages';
import { laneSession, playheadSec, readSides, ref, tempoOf, tempoOfStore, type LaneSession } from './laneSession';
import { openLaneMenu } from './laneGestures';
import { markerNear } from './timeCommands';
import { installWheelSeek } from './wheelSeek';
import { LaneTooltip } from './LaneTooltip';
import { useSession, useStore } from './hooks';
import s from './ChartTimelinePanel.module.css';

const WIDTHS_EXT = 'noteLane.columnWidths';

/** Host options from the settings and the keymap. */
export function hostOptions(services: AppServices): Partial<TimelineHostOptions> {
  const st = services.settings.value;
  const rm = st.general.reducedMotion;
  return {
    translate: (r) => services.i18n.tr(r),
    scrollSensitivity: st.editing.scrollSensitivity,
    wheelPreset: services.keymap.preset,
    altFree: services.keymap.altFree,
    reducedMotion: rm === 'system' ? 'system' : rm === 'on',
  };
}

/** The host's view configuration from the editor store, the settings and the document meter. */
export function viewConfig(services: AppServices): TimelineViewConfig {
  const e = services.editor.getState();
  const tl = e.timeline;
  const meter = services.store?.doc.timing.meter ?? [];
  return {
    pxPerBeat: tl.pxPerBeat,
    bottomBeat: tl.bottomBeat,
    snapDiv: e.snap.div,
    beatSnap: e.snap.beatSnap,
    playheadRatio: services.settings.value.editing.playheadPosition,
    followPlayhead: tl.followPlayhead,
    meter: meter.map((mk) => ({ beat: Beats.toF64(mk.t), beatsPerBar: mk.beatsPerBar })),
    waveformOpacity: tl.waveformOpacity,
  };
}

type Layer = 'static' | 'content' | 'overlay';

/** Which lane layers an editor-store change invalidates. */
export function laneLayersFor(a: EditorStore, b: EditorStore, sides: { readonly changed: boolean }): Layer[] {
  const out = new Set<Layer>();
  if (
    a.snap !== b.snap ||
    a.timeline.laneCenterX !== b.timeline.laneCenterX ||
    a.timeline.laneHalfRange !== b.timeline.laneHalfRange ||
    a.timeline.waveform !== b.timeline.waveform
  ) {
    out.add('static');
    out.add('content');
    out.add('overlay');
  }
  if (
    a.selection.notes !== b.selection.notes ||
    a.alsoShow !== b.alsoShow ||
    a.filters.lockedLines !== b.filters.lockedLines ||
    a.activeLine !== b.activeLine ||
    sides.changed
  ) {
    out.add('content');
    out.add('overlay');
  }
  if (
    a.hover !== b.hover ||
    a.placement !== b.placement ||
    a.tool !== b.tool ||
    a.selection !== b.selection ||
    a.preview !== b.preview
  ) {
    out.add('overlay');
  }
  return [...out];
}

/** Whether an editor-store change alters the set of columns (relayout). */
export function needsRelayout(a: EditorStore, b: EditorStore): boolean {
  return (
    a.activeLine !== b.activeLine ||
    a.pinnedLanes !== b.pinnedLanes ||
    a.pinnedColumns !== b.pinnedColumns ||
    a.focusedColumn !== b.focusedColumn ||
    a.activeLayer !== b.activeLayer ||
    a.timeline.showMotionColumns !== b.timeline.showMotionColumns ||
    a.timeline.expandedColumn !== b.timeline.expandedColumn ||
    a.level !== b.level
  );
}

/** Whole-song data for the overview strip: note and key beats (sorted) and the song extent. */
export function overviewData(
  services: AppServices,
): { start: number; end: number; noteBeats: Float64Array; keyBeats: Float64Array } | null {
  const store = services.store;
  if (store === null) return null;
  const notes = new Float64Array(store.doc.notes.size);
  let i = 0;
  let last = 0;
  for (const n of store.doc.notes.values()) {
    const b = Beats.toF64(n.t);
    notes[i++] = b;
    last = Math.max(last, Beats.toF64(n.end));
  }
  notes.sort();
  const keys = new Float64Array(store.doc.keys.size);
  i = 0;
  for (const k of store.doc.keys.values()) {
    const b = Beats.toF64(k.t);
    keys[i++] = b;
    last = Math.max(last, b);
  }
  keys.sort();
  const music = services.audio.music;
  const tempo = tempoOfStore(store);
  const end = music !== null && music.durationSec > 0 ? Math.max(last, tempo.beatF64At(music.durationSec)) : last;
  return { start: 0, end: Math.max(end, 1), noteBeats: notes, keyBeats: keys };
}

/** Wires a mounted host to the services; returns the disposer. Exported for tests. */
export function connectHost(
  services: AppServices,
  host: TimelineHostController,
  session: LaneSession,
  onLayout: (l: readonly ColumnLayout[]) => void,
): () => void {
  const offs: Array<() => void> = [];
  /** Music, offset and tempo the waveform was built for ('' = none). */
  let waveformKey = '';
  /** Failed attempts for the current music (retried a few times: the audio worker may still be starting). */
  let waveformFailures = 0;
  let waveformMusic = '';
  // ---- columns, decorations, widths ----
  host.setColumnDefs(services.registry.timelineColumns(services));
  host.setDecorations(services.registry.timelineDecorations(services));
  const widths = services.settings.getExt(WIDTHS_EXT);
  if (widths !== undefined && widths !== null && typeof widths === 'object' && !Array.isArray(widths)) {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(widths)) if (typeof v === 'number') out[k] = v;
    host.setColumnWidths(out);
  }
  host.setRevisionSource(() => services.engine?.revision ?? 0);
  host.setView(viewConfig(services));

  // ---- view sync ----
  offs.push(
    host.onViewChange((cfg) => {
      const e = services.editor.getState();
      const tl = e.timeline;
      if (tl.pxPerBeat !== cfg.pxPerBeat || tl.bottomBeat !== cfg.bottomBeat) {
        e.setTimeline({ pxPerBeat: cfg.pxPerBeat, bottomBeat: cfg.bottomBeat });
      }
      if (e.snap.div !== cfg.snapDiv) e.setSnap({ div: cfg.snapDiv });
    }),
  );

  // ---- editor store: view, redraws, relayouts ----
  let lastSides = session.sides;
  offs.push(
    services.editor.subscribe((next, prev) => {
      if (next.timeline !== prev.timeline || next.snap !== prev.snap) {
        host.setView(viewConfig(services));
      }
      const sidesChanged = session.sides !== lastSides;
      lastSides = session.sides;
      const layers = laneLayersFor(prev, next, { changed: sidesChanged });
      for (const id of session.views.keys()) for (const layer of layers) host.invalidate(id, layer);
      if (needsRelayout(prev, next)) host.relayout();
      if (next.timeline.waveform !== prev.timeline.waveform) void refreshWaveform();
    }),
  );

  // ---- settings and keymap ----
  offs.push(
    services.settings.subscribe(() => {
      host.setOptions(hostOptions(services));
      host.setView(viewConfig(services));
      const sides = readSides(services);
      if (sides !== session.sides) {
        session.sides = sides;
        session.redraw(['content', 'overlay']);
        session.emit();
      } else session.redraw(['overlay']);
    }),
  );
  offs.push(
    services.keymap.subscribe(() => {
      host.setOptions(hostOptions(services));
    }),
  );

  // ---- document ----
  let overviewTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleOverview = (): void => {
    if (overviewTimer !== null) clearTimeout(overviewTimer);
    overviewTimer = setTimeout(() => {
      overviewTimer = null;
      host.setOverview(overviewData(services));
    }, 250);
  };
  let offStore: (() => void) | null = null;
  const attachStore = (): void => {
    offStore?.();
    offStore = null;
    const store = services.store;
    host.setView(viewConfig(services));
    host.relayout();
    host.applyChangeSet({ dirty: allDirty() });
    host.setOverview(overviewData(services));
    void refreshWaveform();
    if (store === null) return;
    offStore = store.subscribe((cs: ChangeSet) => {
      host.applyChangeSet(cs);
      const d = cs.dirty;
      if (d.timing || d.tempo) {
        host.setView(viewConfig(services));
        void refreshWaveform();
      }
      const active = services.editor.getState().activeLine;
      if (d.lineSet || (active !== null && d.lineMotion.has(active)) || d.lineProps.size > 0) host.relayout();
      if (d.lineNotes.size > 0 || d.lineMotion.size > 0 || d.tempo || d.lineSet) scheduleOverview();
      if (cs.source === 'load') host.setOverview(overviewData(services));
    });
  };
  attachStore();
  offs.push(services.onChange(attachStore));
  offs.push(() => {
    offStore?.();
    if (overviewTimer !== null) clearTimeout(overviewTimer);
  });

  // ---- waveform (soft dependency on the audio worker, W2-08) ----
  async function refreshWaveform(): Promise<void> {
    const store = services.store;
    const music = services.audio.music;
    const on = services.editor.getState().timeline.waveform;
    if (store === null || music === null || !on) {
      waveformKey = '';
      host.setWaveform(null);
      return;
    }
    const timing = store.doc.timing;
    const offsetSec =
      timing.offsetMs / 1000 + store.doc.pkg.infoOffsetSec + store.doc.editor.previewAudioDelayMs / 1000;
    const key = `${music.asset}|${String(offsetSec)}|${String(store.version('bpm'))}`;
    if (key === waveformKey) return;
    if (music.asset !== waveformMusic) {
      waveformMusic = music.asset;
      waveformFailures = 0;
    }
    waveformKey = key;
    let levels;
    try {
      levels = await services.dsp.call('peaks', { audio: music.asset });
    } catch {
      // The worker may not have the PCM yet: install it (copied: transferring would detach the engine's buffers).
      const channels = services.audio.musicChannels();
      if (channels === null) return;
      try {
        await services.dsp.call('setPcm', {
          audio: music.asset,
          channels: channels.map((c) => c.slice()),
          sampleRate: music.sampleRate,
        });
        levels = await services.dsp.call('peaks', { audio: music.asset });
      } catch {
        waveformFailures++;
        if (waveformFailures < 3) waveformKey = '';
        return;
      }
    }
    if (waveformKey !== key || services.store !== store) return;
    host.setWaveform(
      createWaveformSource({ levels, sampleRate: music.sampleRate, tempo: tempoOfStore(store), offsetSec }),
    );
  }

  // ---- playback ----
  const frameId = 'note-lane:playhead';
  let lastPlaying = services.audio.transport.state.playing;
  host.setPlaying(lastPlaying);
  offs.push(
    services.scheduler.add(frameId, 'transport', () => {
      const tempo = tempoOf(services);
      if (tempo !== null) host.setPlayhead(tempo.beatF64At(playheadSec(services)));
      const e = services.editor.getState();
      // The hold being placed at the playhead and paste ghosts anchored to it follow playback.
      if (e.placement !== null) session.redraw(['overlay']);
    }),
  );
  services.scheduler.request(frameId);
  offs.push(
    services.audio.transport.onChange((st) => {
      if (st.playing !== lastPlaying) {
        lastPlaying = st.playing;
        host.setPlaying(st.playing);
      }
      // Cheap when nothing changed (keyed on the music, offset and tempo).
      void refreshWaveform();
      services.scheduler.request(frameId);
    }),
  );
  // The music decodes after the project opened: pick its waveform up once it is there.
  const musicPoll = setInterval(() => {
    if (services.audio.music !== null && waveformKey === '' && services.editor.getState().timeline.waveform) {
      void refreshWaveform();
    }
  }, 1000);
  offs.push(() => {
    clearInterval(musicPoll);
  });

  // ---- RPE wheel seek (UX-036): the wheel moves the playhead with the view ----
  offs.push(
    installWheelSeek(services, host, () => {
      services.scheduler.request(frameId);
    }),
  );

  // ---- host events ----
  offs.push(
    host.on('seek', (e) => {
      const tempo = tempoOf(services);
      if (tempo === null) return;
      services.audio.transport.seek(Math.max(0, tempo.secAtBeatF64(Math.max(0, e.beat))));
      services.scheduler.request(frameId);
    }),
  );
  let marqueeBase: Selection | null = null;
  offs.push(
    host.on('marquee', (e) => {
      const ed = services.editor.getState();
      if (e.phase === 'cancel') {
        if (marqueeBase !== null) ed.select(marqueeBase, 'replace');
        marqueeBase = null;
        return;
      }
      if (marqueeBase === null) marqueeBase = ed.selection;
      const items = { notes: new Set(e.items.notes), keys: new Set(e.items.keys), segments: new Set(e.items.segments) };
      const next = Sel.combine(marqueeBase, items, e.mode);
      ed.select(next, 'replace');
      if (e.phase === 'end') marqueeBase = null;
    }),
  );
  offs.push(
    host.on('loop', (e) => {
      if (e.phase !== 'commit') return;
      services.editor.getState().setLoop(e.range === null ? null : { start: e.range.start, end: e.range.end });
    }),
  );
  offs.push(
    host.on('gutterContextMenu', (e) => {
      session.gutter = { beat: e.beat, marker: markerNear(services, e.beat, host.axis.pxPerBeat) };
      session.lastContextEvent = null;
      session.client = { x: e.clientX, y: e.clientY };
      openLaneMenu(services, 'timing.context');
    }),
  );
  offs.push(
    host.on('columnResize', () => {
      services.settings.setExt(WIDTHS_EXT, { ...host.columnWidths() });
    }),
  );
  offs.push(
    host.on('layout', (list) => {
      for (const l of list) {
        const el = host.root?.querySelector<HTMLElement>(`[data-tl-col="${CSS.escape(l.id)}"]`) ?? null;
        if (el === null) continue;
        // Instances declare their own region; the host's built-in columns (gutter, waveform) are the lane's.
        if (el.dataset.region !== undefined) continue;
        el.dataset.region = 'lane';
        if (!el.hasAttribute('tabindex')) el.tabIndex = -1;
      }
      onLayout(list);
    }),
  );
  onLayout(host.getLayout());

  return () => {
    for (const off of offs.splice(0)) off();
  };
}

function allDirty(): ChangeSet['dirty'] {
  return {
    tempo: true,
    timing: true,
    meta: true,
    pkg: true,
    editor: true,
    rpeRoot: true,
    lineSet: true,
    lineProps: new Set(),
    lineMotion: new Map(),
    lineNotes: new Set(),
    effects: true,
    markers: true,
    groups: true,
    assets: true,
    generators: true,
  };
}

interface HeaderPortal {
  readonly id: string;
  readonly el: HTMLElement;
  readonly Header: ComponentType<{ readonly instanceId: string }>;
}

export function ChartTimelinePanel(props: PanelProps) {
  const { services } = props;
  const t = useT();
  const container = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const [headers, setHeaders] = useState<readonly HeaderPortal[]>([]);
  const store = useStore(services);
  useSession(services);

  useEffect(() => {
    const el = container.current;
    if (el === null) return undefined;
    const session = laneSession(services);
    const host = createTimelineHost({ scheduler: services.scheduler, options: hostOptions(services) });
    session.host = host;
    host.mount(el);
    const disconnect = connectHost(services, host, session, (list) => {
      setHeaders(
        list.flatMap((l) =>
          l.header === null || l.headerEl === null ? [] : [{ id: l.id, el: l.headerEl, Header: l.header }],
        ),
      );
    });
    services.attachTimeline(host);
    return () => {
      disconnect();
      services.attachTimeline(null);
      if (session.host === host) session.host = null;
      session.views.clear();
      session.pointer = null;
      host.dispose();
    };
  }, [services]);

  // Pointer bookkeeping around the host (client position, right clicks, focus regions).
  useEffect(() => {
    const el = root.current;
    if (el === null) return undefined;
    const session = laneSession(services);
    const onMove = (e: PointerEvent): void => {
      session.client = { x: e.clientX, y: e.clientY };
      // Paste ghosts and R ... R holds follow the mouse over every column, not only over the lanes.
      if (services.editor.getState().placement !== null) session.redraw(['overlay']);
    };
    const onDown = (e: PointerEvent): void => {
      session.client = { x: e.clientX, y: e.clientY };
      // A new left press ends the context of the last right click (context-menu commands act on it until then).
      if (e.button === 0) {
        session.context = null;
        session.gutter = null;
      }
      const target = e.target instanceof Element ? e.target : null;
      const col = target?.closest<HTMLElement>('[data-tl-col]') ?? null;
      if (col !== null && document.activeElement !== col && !col.contains(document.activeElement)) {
        col.focus({ preventScroll: true });
      }
    };
    const onContext = (e: MouseEvent): void => {
      session.lastContextEvent = e;
      session.client = { x: e.clientX, y: e.clientY };
    };
    const onLeave = (): void => {
      if (session.tooltip !== null) {
        session.tooltip = null;
        session.emit();
      }
    };
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('contextmenu', onContext, true);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('contextmenu', onContext, true);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [services]);

  return (
    <div
      ref={root}
      className={s.panel}
      data-region="lane"
      data-help-id="timeline"
      aria-label={t(m.laneRegion)}
      role="region"
    >
      <div ref={container} className={s.host} />
      {store === null && (
        <div className={s.empty}>
          <EmptyState icon="AudioLines" title={ref(m.noLineTitle)} body={ref(m.reasonNoProject)} />
        </div>
      )}
      {headers.map((h) => createPortal(<h.Header instanceId={h.id} />, h.el, h.id))}
      <LaneTooltip services={services} />
    </div>
  );
}
